/**
 * DOCUMENTOS DE VERIFICAÇÃO (privados) — decisões do usuário.
 *
 * Duas telas na mesma página:
 *   * modo "pasta"  → a lista de conferência do cadastro que JÁ existe, com o
 *     envio de cada documento, a situação de cada item e o motivo da correção;
 *   * modo "fila"   → quem analisa (federação com o ok do super_admin, ou o
 *     super_admin) vê o que está esperando e decide: aprovar / corrigir / recusar.
 *
 * O arquivo NUNCA tem endereço público: aqui ele é buscado com a sessão do
 * usuário e aberto a partir da memória.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { documentosApi, federationApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DialogoMotivo from '../components/common/DialogoMotivo';
import {
  AlertTriangle, CheckCircle2, Clock, Eye, FileText, Loader2, ShieldCheck,
  Upload, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const SELO = {
  nao_enviado: { texto: 'Nenhum documento enviado', cor: 'text-[#A1A1AA]', icone: FileText },
  aguardando: { texto: 'Aguardando verificação', cor: 'text-amber-300', icone: Clock },
  corrigir: { texto: 'Correção necessária', cor: 'text-red-400', icone: AlertTriangle },
  verificado: { texto: 'Verificado', cor: 'text-emerald-400', icone: ShieldCheck },
  enviado: { texto: 'Aguardando verificação', cor: 'text-amber-300', icone: Clock },
  aprovado: { texto: 'Aprovado', cor: 'text-emerald-400', icone: CheckCircle2 },
  recusado: { texto: 'Recusado', cor: 'text-red-400', icone: XCircle },
};

const enviadoEm = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');

function Selo({ situacao, grande = false }) {
  const dado = SELO[situacao] || SELO.nao_enviado;
  const Icone = dado.icone;
  return (
    <span className={`inline-flex items-center gap-1.5 ${dado.cor} ${grande ? 'text-base font-semibold' : 'text-xs font-semibold'}`}
          data-testid={`selo-${situacao}`}>
      <Icone size={grande ? 18 : 14} /> {dado.texto}
    </span>
  );
}

function Pastas() {
  const { user } = useAuth();
  const { search } = useLocation();
  const parametros = new URLSearchParams(search);

  // De quem é a pasta: pelos parâmetros da URL; sem eles, o professor cai na
  // pasta DELE e a federação, na pasta da federação vinculada.
  const [alvo, setAlvo] = useState(null);
  /* perfil equipe sem equipe aprovada: explica em vez de abrir pasta vazia */
  const [semEquipe, setSemEquipe] = useState(false);
  const entityType = alvo?.entity_type || 'professor';
  const entityId = alvo?.entity_id || '';

  useEffect(() => {
    const tipo = parametros.get('entity_type');
    const id = parametros.get('entity_id');
    if (tipo && id) { setAlvo({ entity_type: tipo, entity_id: id }); return; }
    if (user?.role === 'team') {
      documentosApi.minhaPasta()
        .then((resposta) => {
          const dados = resposta.data || {};
          if (dados.entity_type && dados.entity_id) {
            setAlvo({ entity_type: dados.entity_type, entity_id: dados.entity_id });
          } else {
            setSemEquipe(true);
          }
        })
        .catch(() => setSemEquipe(true));
      return;
    }
    if (user?.role === 'federation') {
      federationApi.mine()
        .then((resposta) => {
          const federacao = resposta.data?.federation;
          setAlvo(federacao
            ? { entity_type: 'federacao', entity_id: federacao._id }
            : { entity_type: 'professor', entity_id: user?._id || '' });
        })
        .catch(() => setAlvo({ entity_type: 'professor', entity_id: user?._id || '' }));
      return;
    }
    setAlvo({ entity_type: 'professor', entity_id: user?._id || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, user]);

  const [pasta, setPasta] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState('');
  const [acessos, setAcessos] = useState(null);

  const carregar = useCallback(async () => {
    if (semEquipe) {
      return (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-6 space-y-3" data-testid="documentos-sem-equipe">
            <p className="text-white font-semibold">Sua equipe ainda nao foi aprovada</p>
            <p className="text-sm text-[#A1A1AA]">
              Os documentos sao enviados depois que a federacao ou o administrador aprova o
              cadastro da sua equipe. Acompanhe o pedido em Minha Equipe.
            </p>
            <Button asChild size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Link to="/minha-equipe">Ir para Minha Equipe</Link>
            </Button>
          </CardContent>
        </Card>
      );
    }

    if (!entityId) { setCarregando(false); return; }
    setCarregando(true);
    try {
      const { data } = await documentosApi.pasta(entityType, entityId);
      setPasta(data);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao carregar os documentos');
    } finally {
      setCarregando(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { carregar(); }, [carregar]);

  const enviar = async (tipo, arquivo) => {
    if (!arquivo) return;
    setEnviando(tipo);
    try {
      await documentosApi.enviar(entityType, entityId, tipo, arquivo);
      toast.success('Documento enviado para análise');
      await carregar();
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao enviar o documento');
    } finally {
      setEnviando('');
    }
  };

  const abrir = async (tipo) => {
    try {
      const { data } = await documentosApi.arquivo(entityType, entityId, tipo);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao abrir o documento');
    }
  };

  const verAcessos = async () => {
    try {
      const { data } = await documentosApi.acessos(entityType, entityId);
      setAcessos(data.acessos || []);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao carregar os acessos');
    }
  };

  if (!entityId) {
    return (
      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-6 space-y-3">
          <p className="text-white font-semibold">Escolha de quem são os documentos</p>
          <p className="text-sm text-[#A1A1AA]">
            A pasta de documentos fica dentro do cadastro que já existe. Abra esta tela a partir
            do cadastro (equipe ou federação) para enviar os documentos dele.
          </p>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Link to="/minha-equipe">Minha Equipe</Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Link to="/federations">Federações</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (carregando) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>;
  }
  if (!pasta) return null;

  const rotulo = { professor: 'Professor', equipe: 'Equipe', federacao: 'Federação' }[entityType];

  return (
    <div className="space-y-4" data-testid="pasta-documentos">
      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-6 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#71717A]">{rotulo}</p>
              <h2 className="text-xl font-bold text-white">{pasta.nome}</h2>
            </div>
            <Selo situacao={pasta.situacao} grande />
          </div>
          <p className="text-sm text-[#A1A1AA]">
            {pasta.aprovados} de {pasta.total} documentos aprovados.
            {pasta.analista === 'super_admin'
              ? ' Quem analisa é o Super Admin.'
              : ' Quem analisa é a federação vinculada.'}
            {' '}O selo é informativo: não bloqueia nada no sistema.
          </p>
          {pasta.pode_enviar && (
            <p className="text-xs text-[#71717A]">
              Aceita JPG, PNG ou PDF (até 15 MB). O arquivo é comprimido, guardado em área privada
              e sai do servidor — ninguém chega nele por link.
            </p>
          )}
        </CardContent>
      </Card>

      {(pasta.alertas || []).length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-4 space-y-1">
            <p className="text-sm font-semibold text-amber-200">Pontos para conferir no cadastro</p>
            {pasta.alertas.map((a) => (
              <p key={a} className="text-xs text-amber-200/90">• {a}</p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-white">Documentos</h3>
          <ul className="divide-y divide-[#27272A]" data-testid="lista-documentos">
            {pasta.itens.map((item) => (
              <li key={item.tipo} className="py-4 space-y-2" data-testid={`item-${item.tipo}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-white">{item.nome}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <Selo situacao={item.status} />
                      {item.enviado_em && (
                        <span className="text-xs text-[#71717A]">enviado em {enviadoEm(item.enviado_em)}</span>
                      )}
                    </div>
                    {item.motivo && (
                      <p className="text-xs text-red-300 mt-1">Motivo: {item.motivo}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {item.documento && (
                      <Button size="sm" variant="outline" onClick={() => abrir(item.tipo)}
                              data-testid={`ver-${item.tipo}`}
                              className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                        <Eye size={16} className="mr-1" /> Ver
                      </Button>
                    )}
                    {pasta.pode_enviar && (
                      <label
                        data-testid={`enviar-${item.tipo}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#D4AF37]/50 px-3 py-1.5 text-xs font-bold text-[#D4AF37] cursor-pointer hover:bg-[#D4AF37]/10"
                      >
                        {enviando === item.tipo
                          ? <Loader2 size={16} className="animate-spin" />
                          : <Upload size={16} />}
                        {item.documento ? 'Substituir' : 'Enviar'}
                        <Input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="hidden"
                          data-testid={`arquivo-${item.tipo}`}
                          onChange={(e) => enviar(item.tipo, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="pt-2 border-t border-[#27272A]">
            {acessos === null ? (
              <Button size="sm" variant="outline" onClick={verAcessos}
                      data-testid="ver-acessos"
                      className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                Quem abriu meus documentos
              </Button>
            ) : (
              <div className="space-y-1" data-testid="lista-acessos">
                <p className="text-xs uppercase tracking-wider text-[#71717A]">Acessos registrados</p>
                {acessos.length === 0 ? (
                  <p className="text-xs text-[#71717A]">Ninguém abriu os arquivos ainda.</p>
                ) : acessos.map((a, i) => (
                  <p key={`${a.em}-${i}`} className="text-xs text-[#A1A1AA]">
                    {a.por} abriu em {String(a.em || '').slice(0, 16).replace('T', ' ')}
                  </p>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Fila() {
  const [fila, setFila] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [decidindo, setDecidindo] = useState('');
  const [aberto, setAberto] = useState('');
  // Fase 2 (item 7): qual documento/decisão está esperando o motivo no diálogo.
  const [pedido, setPedido] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await documentosApi.fila();
      setFila(data.fila || []);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao carregar a fila');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Fase 2 (item 7): o motivo passou a ser pedido no diálogo do sistema, com o
  // MESMO efeito de antes — cancelar não decide nada e confirmar vazio vale como
  // "sem motivo". O envio continua idêntico (mesma API, mesmo aviso de sucesso).
  const decidir = (documento, decisao) => {
    if (decisao !== 'aprovado') {
      setPedido({ documento, decisao });
      return;
    }
    aplicarDecisao(documento, decisao, '');
  };

  const aplicarDecisao = async (documento, decisao, motivo) => {
    setPedido(null);
    setDecidindo(documento.id);
    try {
      await documentosApi.decidir(documento.id, decisao, motivo);
      toast.success(
        decisao === 'aprovado' ? 'Documento aprovado'
          : decisao === 'corrigir' ? 'Correção solicitada' : 'Documento recusado');
      await carregar();
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao decidir');
    } finally {
      setDecidindo('');
    }
  };

  // O dono do arquivo (tipo de cadastro + id) está no GRUPO, não no item — o item
  // só tem tipo/nome/status. Antes esta linha lia `item.entity_type`, que não
  // existe, e o endereço saía como
  //   /api/documentos/undefined/undefined/<tipo>/arquivo  ->  400
  // por isso "Abrir" falhava na fila. É o mesmo caminho que a tela da pasta já
  // usa (props `entityType`/`entityId`), então o comportamento fica igual.
  const abrir = async (grupo, item) => {
    try {
      const { data } = await documentosApi.arquivo(grupo.entity_type, grupo.entity_id, item.tipo);
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || 'Erro ao abrir o documento');
    }
  };

  if (carregando) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>;
  }

  return (
    <div className="space-y-4" data-testid="fila-documentos">
      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold text-white">Documentos para analisar</h2>
          <p className="text-sm text-[#A1A1AA] mt-1">
            A conferência automática aponta os pontos duvidosos; a decisão é sempre sua.
            {fila.length === 0 ? ' Nada esperando por você agora.' : ` ${fila.length} cadastro(s) aguardando.`}
          </p>
        </CardContent>
      </Card>

      {fila.map((grupo) => (
        <Card key={`${grupo.entity_type}-${grupo.entity_id}`} className="bg-[#141414] border-[#27272A]"
              data-testid={`grupo-${grupo.entity_id}`}>
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#71717A]">{grupo.entity_type}</p>
                <p className="text-white font-bold">{grupo.nome}</p>
              </div>
              <Button size="sm" variant="outline"
                      onClick={() => setAberto(aberto === grupo.entity_id ? '' : grupo.entity_id)}
                      className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                {aberto === grupo.entity_id ? 'Fechar' : `Analisar (${grupo.enviados})`}
              </Button>
            </div>

            {grupo.alertas?.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
                {grupo.alertas.map((a) => (
                  <p key={a} className="text-micro text-amber-200">• {a}</p>
                ))}
              </div>
            )}

            {aberto === grupo.entity_id && (
              <ul className="divide-y divide-[#27272A]">
                {grupo.itens.map((item) => (
                  <li key={item.tipo} className="py-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-white">{item.nome}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Selo situacao={item.status} />
                        {item.enviado_em && (
                          <span className="text-xs text-[#71717A]">em {enviadoEm(item.enviado_em)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {item.documento && (
                        <Button size="sm" variant="outline" onClick={() => abrir(grupo, item)}
                                data-testid={`abrir-${item.tipo}`}
                                className="border-[#27272A] text-[#A1A1AA] hover:text-white">
                          <Eye size={16} className="mr-1" /> Abrir
                        </Button>
                      )}
                      {item.documento && item.status !== 'aprovado' && (
                        <>
                          <Button size="sm" onClick={() => decidir(item.documento, 'aprovado')}
                                  disabled={decidindo === item.documento.id}
                                  data-testid={`aprovar-${item.tipo}`}
                                  className="bg-emerald-600 text-white hover:bg-emerald-500 font-bold">
                            <CheckCircle2 size={16} className="mr-1" /> Aprovar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decidir(item.documento, 'corrigir')}
                                  disabled={decidindo === item.documento.id}
                                  data-testid={`corrigir-${item.tipo}`}
                                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                            Corrigir
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decidir(item.documento, 'recusado')}
                                  disabled={decidindo === item.documento.id}
                                  data-testid={`recusar-${item.tipo}`}
                                  className="border-[#27272A] text-red-400 hover:text-red-300">
                            Recusar
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}

      <DialogoMotivo
        aberto={!!pedido}
        titulo={pedido?.decisao === 'corrigir' ? 'O que precisa ser corrigido?' : 'Motivo da recusa:'}
        descricao={pedido?.decisao === 'corrigir'
          ? 'Explique o que o responsável precisa corrigir. Ele recebe esta mensagem junto do documento.'
          : 'Explique por que o documento está sendo recusado. O responsável verá esta mensagem.'}
        rotulo="Motivo"
        placeholder={pedido?.decisao === 'corrigir'
          ? 'Ex.: a foto do RG está cortada nas bordas'
          : 'Ex.: documento ilegível, não dá para conferir os dados'}
        textoConfirmar={pedido?.decisao === 'corrigir' ? 'Pedir correção' : 'Recusar'}
        varianteConfirmar={pedido?.decisao === 'corrigir' ? 'default' : 'destructive'}
        aoConfirmar={(texto) => pedido && aplicarDecisao(pedido.documento, pedido.decisao, texto)}
        aoCancelar={() => setPedido(null)}
        testId="dialogo-documento"
      />
    </div>
  );
}

export default function DocumentosPage({ modo = 'pasta' }) {
  return (
    <MainLayout>
      <div className="space-y-6" data-testid="documentos-page">
        <CabecalhoPagina
          contexto="Documentos"
          titulo={modo === 'fila' ? 'Analisar documentos' : 'Documentos'}
          descricao={
            modo === 'fila'
              ? 'Confira o que foi enviado e decida: aprovar, pedir correção ou recusar.'
              : 'Envie os documentos que comprovam o que já está no seu cadastro.'
          }
          icone={modo === 'fila' ? ShieldCheck : FileText}
        />
        {modo === 'fila' ? <Fila /> : <Pastas />}
      </div>
    </MainLayout>
  );
}
